import { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, Card, CardContent, CircularProgress, Tab, Tabs, Typography } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SyncIcon from "@mui/icons-material/Sync"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"

import { ChipStyled } from "src/shared/components"
import { usePermission } from "src/shared/hooks/usePermission"
import ROUTE_PATHS from "src/routes/route-path"
import {
  useIntegrationQuery,
  useStartIntegrationAuthorizationMutation,
  IntegrationOperationsDocument,
  IntegrationProductsDocument,
  useSyncIntegrationCatalogMutation,
  useSyncIntegrationProductsMutation
} from "src/graphql/quark/quark.type"
import { AUTH_ERROR_PARAM, AUTH_FLOW_OAUTH, FEATURE_PRODUCT_CREATE } from "../../connectorFeatures"
import { readStockBuffer } from "../../stockBuffer"
import ConfigureTab from "../../components/ConfigureTab"
import EventsTab from "../../components/EventsTab"
import LocationsTab from "../../components/LocationsTab"
import ProductsTab from "../../components/ProductsTab"
import ActivityTab from "../../components/ActivityTab"

// Carrier connectors have their own module (plan Phase 4) — cost-centre
// mapping doesn't fit the flat settings/products/stores shape of this page.
const CARRIER_CONNECTOR_CODES = new Set(["TCS", "Trax", "Leopards"])

const statusColor = (status: string) => {
  if (status === "ACTIVE") return "success"
  if (status === "NEEDS_REAUTH" || status === "REVOKED") return "error"
  return "warning"
}

const IntegrationDetail = () => {
  const { connectionId = "" } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = usePermission()
  const canEdit = hasPermission("system:integrations:update")

  const [tab, setTab] = useState(0)

  const [searchParams, setSearchParams] = useSearchParams()
  const authorizationError = searchParams.get(AUTH_ERROR_PARAM)
  const shownAuthorizationError = useRef<string | null>(null)

  useEffect(() => {
    if (!authorizationError || shownAuthorizationError.current === authorizationError) return
    shownAuthorizationError.current = authorizationError
    toast.error(authorizationError)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(AUTH_ERROR_PARAM)
        return next
      },
      { replace: true }
    )
  }, [authorizationError, setSearchParams])

  const { data, loading } = useIntegrationQuery({
    variables: { connectionId },
    fetchPolicy: "network-only",
    skip: !connectionId
  })

  const integrationConnectorCode = data?.fusionIntegration?.connectorCode

  useEffect(() => {
    if (integrationConnectorCode && CARRIER_CONNECTOR_CODES.has(integrationConnectorCode))
      navigate(ROUTE_PATHS.FULFILLMENT_ORDERS.CARRIER_DETAIL(connectionId), {
        replace: true
      })
  }, [integrationConnectorCode, connectionId, navigate])

  const refetchQueries = [
    { query: IntegrationProductsDocument, variables: { connectionId } },
    { query: IntegrationOperationsDocument, variables: { connectionId } }
  ]
  const [syncCatalog, { loading: syncingStock }] = useSyncIntegrationCatalogMutation({ refetchQueries })
  const [syncProducts, { loading: syncingProducts }] = useSyncIntegrationProductsMutation({ refetchQueries })
  const syncing = syncingStock || syncingProducts
  const [startAuthorization, { loading: authorizing }] = useStartIntegrationAuthorizationMutation()

  const features = data?.fusionIntegration?.features ?? []
  const createsProducts = features.includes(FEATURE_PRODUCT_CREATE)

  const handleAuthorize = async () => {
    try {
      const result = await startAuthorization({
        variables: {
          input: { connectionId, returnUrl: `${window.location.origin}${window.location.pathname}` }
        }
      })
      const authorizeUrl = result.data?.startFusionIntegrationAuthorization.authorizeUrl
      if (authorizeUrl) window.location.assign(authorizeUrl)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not start the connection")
    }
  }

  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`

  /** Pushes products first, then says exactly what went — never "going out" when nothing did. */
  const handleProductSync = async (): Promise<boolean> => {
    const channel = data?.fusionIntegration?.displayName ?? "the channel"
    const result = (await syncProducts({ variables: { connectionId } })).data?.syncFusionIntegrationProducts
    if (!result) return false

    if (result.busy) {
      toast.info(`Products are being pushed to ${channel} right now. Try again in a moment.`)
      return false
    }
    if (result.productUpdatesOff && result.pushed === 0 && result.failed === 0 && result.remaining > 0) {
      toast.warning(
        `${plural(result.remaining, "product")} waiting: "Product updated" is switched off in the Events tab, so none were sent.`
      )
      return true
    }
    if (result.failed > 0)
      toast.error(
        `${plural(result.failed, "product")} failed${result.pushed ? `, ${result.pushed} sent` : ""}: ${result.lastError ?? "see the Products tab"}`
      )
    else if (result.pushed > 0)
      toast.success(
        `Sent ${plural(result.pushed, "product")} to ${channel}` +
          (result.remaining > 0 ? `; ${result.remaining} more going out over the next few minutes.` : ".")
      )
    else if (result.remaining > 0)
      toast.info(`${plural(result.remaining, "product")} queued for ${channel}; going out over the next few minutes.`)
    else toast.success(`Products on ${channel} are already up to date.`)
    return true
  }

  const handleSync = async () => {
    try {
      if (createsProducts && !(await handleProductSync())) return

      const result = await syncCatalog({ variables: { connectionId } })
      const results = result.data?.syncFusionIntegrationCatalog ?? []
      // No store linked, or nothing on the channel yet: there is no stock to send.
      if (createsProducts && results.length === 0) return

      const failed = results.filter((r) => r.error)
      if (failed.length > 0) {
        toast.error(failed[0].error ?? "Sync failed")
        return
      }

      // Only products whose stock or price changed are sent; anything over the
      // rate limit stays queued and the worker sends it shortly.
      const sent = results.reduce((n, r) => n + r.itemCount, 0)
      const queued = results.reduce((n, r) => n + r.queuedCount, 0)
      const what = createsProducts ? "stock level" : "changed product"
      if (sent === 0 && queued === 0)
        toast.success(createsProducts ? "Stock is already up to date." : "Everything is already up to date.")
      else if (queued === 0) toast.success(`Sent ${plural(sent, what)}.`)
      else toast.info(`Sent ${plural(sent, what)}; ${queued} more queued and going out over the next few minutes.`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not sync")
    }
  }

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    )

  const integration = data?.fusionIntegration
  if (!integration)
    return (
      <Box p={2}>
        <Typography>Integration not found.</Typography>
      </Box>
    )

  const isOAuth = integration.authFlow === AUTH_FLOW_OAUTH
  const needsAuthorization = isOAuth && integration.status !== "ACTIVE"
  const reconnecting = integration.status === "NEEDS_REAUTH" || integration.status === "REVOKED"
  // Nothing is sent for a provider-authorised connection until access is granted.
  const canSync = canEdit && (!isOAuth || integration.status === "ACTIVE")

  return (
    <Box p={2}>
      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 1 }}
        onClick={() => navigate(ROUTE_PATHS.INTEGRATIONS_HUB.BASE)}
      >
        All integrations
      </Button>

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Typography variant="h6">{integration.displayName}</Typography>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          disabled={!canSync || syncing}
          onClick={() => void handleSync()}
        >
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </Box>

      {needsAuthorization && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={!canEdit || authorizing}
              onClick={() => void handleAuthorize()}
            >
              {reconnecting ? "Reconnect" : `Connect on ${integration.connectorCode}`}
            </Button>
          }
        >
          {reconnecting
            ? `${integration.connectorCode} access was withdrawn — reconnect the store.`
            : `Finish connecting on ${integration.connectorCode}.`}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Environment
            </Typography>
            <Typography>{integration.environment}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <ChipStyled
                size="small"
                variant="outlined"
                label={integration.status}
                color={statusColor(integration.status)}
              />
            </Box>
          </Box>
          {integration.externalAccountId && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Account
              </Typography>
              <Typography>{integration.externalAccountId}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Published
            </Typography>
            <Typography>
              {integration.mappedProductCount} product
              {integration.mappedProductCount === 1 ? "" : "s"} across {integration.mappedLocationCount} store
              {integration.mappedLocationCount === 1 ? "" : "s"}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Configure" />
        <Tab label="Stores" />
        <Tab label="Products" />
        <Tab label="Events" />
        <Tab label="Activity" />
      </Tabs>

      {tab === 0 && <ConfigureTab integration={integration} canEdit={canEdit} />}
      {tab === 1 && (
        <LocationsTab
          connectionId={connectionId}
          channelName={integration.connectorCode}
          features={integration.features}
          canEdit={canEdit}
        />
      )}
      {tab === 2 && (
        <ProductsTab
          connectionId={connectionId}
          integrationName={integration.displayName}
          defaultStockBuffer={readStockBuffer(integration.settings)}
          features={integration.features}
          canEdit={canEdit}
        />
      )}
      {tab === 3 && <EventsTab connectionId={connectionId} canEdit={canEdit} />}
      {tab === 4 && <ActivityTab connectionId={connectionId} />}
    </Box>
  )
}

export default IntegrationDetail
